// utils/book.ts — the reader's own pages, read out of the reflection archive.
//
// Reflections are saved newest-first in `breatheArchive` (see Reflect.tsx).
// A book, though, reads front-to-back — so page 1 is the earliest reflection.
// These helpers translate between the two orders so the rest of the app can
// think purely in "pages".

export interface BookPage {
  id: string;
  date: string; // ISO string
  title: string; // the reflection's highlight
  body: string; // the AI-narrated summary — the page's prose
}

interface ArchiveEntry {
  date: string;
  highlight: string;
  aiSummary: string;
}

function readArchive(): ArchiveEntry[] {
  try {
    const raw = localStorage.getItem("breatheArchive");
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/** All of the reader's pages, oldest-first (the order a book is read in). */
export function getBookPages(): BookPage[] {
  return [...readArchive()]
    .reverse()
    .map((e, i) => ({
      id: e?.date || String(i),
      date: e?.date,
      title: e?.highlight || "An untitled page",
      body: e?.aiSummary || "",
    }));
}

/** Remove a single page, addressed by its position in the reader's book. */
export function deleteBookPage(pageIndex: number): BookPage[] {
  const archive = readArchive();
  // pages are reversed, so page `pageIndex` maps to the tail of the archive.
  const archiveIndex = archive.length - 1 - pageIndex;
  if (archiveIndex >= 0 && archiveIndex < archive.length) {
    archive.splice(archiveIndex, 1);
    localStorage.setItem("breatheArchive", JSON.stringify(archive));
  }
  return getBookPages();
}

/** A gentle, human date for the top of a page — "Tuesday, April 12"
 *  (with the year appended only when it isn't the current year). */
export function formatFolio(iso: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const opts: Intl.DateTimeFormatOptions = {
    weekday: "long",
    month: "long",
    day: "numeric",
  };
  if (d.getFullYear() !== new Date().getFullYear()) opts.year = "numeric";
  return d.toLocaleDateString(undefined, opts);
}

/** A short label for when the book began — "begun April 12"
 *  (or "begun April 12, 2024" if the book started in an earlier year). */
export function formatBegun(pages: BookPage[]): string {
  const first = pages[0];
  if (!first?.date) return "";
  const d = new Date(first.date);
  if (isNaN(d.getTime())) return "";
  const opts: Intl.DateTimeFormatOptions = { month: "long", day: "numeric" };
  if (d.getFullYear() !== new Date().getFullYear()) opts.year = "numeric";
  return `begun ${d.toLocaleDateString(undefined, opts)}`;
}

/** Download a single page as a plain-text keepsake. */
export function exportBookPage(page: BookPage): void {
  const when = page.date ? new Date(page.date).toLocaleString() : "";
  const content = `${page.title}\n${when}\n\n${page.body}\n`;
  const blob = new Blob([content], { type: "text/plain;charset=utf-8" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = `breathe-page-${page.date || Date.now()}.txt`;
  link.click();
  URL.revokeObjectURL(link.href);
}
